import { getCompanyUsersCount } from '@/components/account/api';
import { css } from '@emotion/react';
import { Divider } from 'antd';
import moment from 'moment';
import { CSSProperties } from 'react';
import { Plan, Product } from '../models';
import { cn, formatNumberWithDecimals } from '@arcsite/utils';

export const renderPlan = (plan: Plan, plans: Plan[]) => {
  if (plan.interval === 'month') {
    return (
      <div className="flex flex-col">
        <span className="mr-4">
          {<Money value={plan.amount} />}/
          <span className="capitalize">{plan.interval}</span>
        </span>
        <span className="text-12 text-black-55 mt-2">Billed Monthly</span>
      </div>
    );
  }
  if (plan.interval === 'year') {
    const monthlyAmount = plans.find(
      plan => plan.interval === 'month'
    )?.amount!;
    const save = formatNumberWithDecimals(
      ((monthlyAmount * 12 - plan.amount) / (monthlyAmount * 12)) * 100,
      0
    );
    return (
      <div className="flex justify-between w-full">
        <div className="flex flex-col">
          <span className="mr-4">
            {<Money value={plan.amount / 12} />}/Month
          </span>
          <span className="text-12 text-black-55 mt-2">
            <Money value={plan.amount} />/
            <span className="capitalize">{plan.interval}</span>, Billed Yearly
          </span>
        </div>
        <span className="text-orange-e3571c text-12">Save {save}%</span>
      </div>
    );
  }
  return null;
};

export function Field({
  children,
  className = '',
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={style}
      className={cn(
        'flex flex-col sm:flex-row mb-5 sm:mb-8 sm:items-center',
        className
      )}
    >
      {children}
    </div>
  );
}

interface LabelProps {
  children?: React.ReactNode;
  className?: string;
  htmlFor?: string;
  width?: number | string;
}

export function Label({ children, className, width, ...props }: LabelProps) {
  const style = width
    ? {
        width:
          typeof width === 'number'
            ? `${width}px`
            : width.endsWith('px')
              ? width
              : `${width}px`,
      }
    : {};
  return (
    <label
      style={style}
      className={className + ' w-72 font-semibold text-normal mb-1'}
      {...props}
    >
      {children}
    </label>
  );
}

export function FooterSperator() {
  return (
    <Divider
      css={css`
        transform: scaleX(1.1);
        margin-bottom: 10px;
      `}
    />
  );
}

export function Error({ children }: { children: React.ReactNode }) {
  return <span className="text-red-600">{children}</span>;
}

export function Money({ value }: { value: number }) {
  return (
    <span>
      {(value / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })}
    </span>
  );
}

export function FormattedDate({ val }: { val: number }) {
  return <span>{moment(val * 1000).format('ll')}</span>;
}

export function FormattedInterval({ value }: { value: 'month' | 'year' }) {
  return <span>{value == 'month' ? 'Monthly' : 'Yearly'}</span>;
}

export async function validateSeatQuantity(
  quantity: number
): Promise<[boolean, string]> {
  const { count } = await getCompanyUsersCount();
  if (count > quantity) {
    return [
      false,
      `Number of Seats can not be less than the number of company users (${count})`,
    ];
  }
  return [true, ''];
}

export const HIDDEN_LEVELS = ['ESSENTIAL', 'PREMIUM'];
export const VISIBLE_LEVELS = [
  'FREE',
  'BASIC',
  'PROFESSIONAL',
  'TAKEOFF_BASIC',
  'PREMIUM_PLUS',
];

export type VisibleLevels = (typeof VISIBLE_LEVELS)[number];

export type ProductAdditionalInfo = {
  level: number;
  subTitle?: string;
  displayName: string;
  description: string;
  featureTitle: string;
  features: string[];
  popular: boolean;
};

export type ProductWithAdditionalInfo = Product & ProductAdditionalInfo;

export const PRODUCT_LEVEL_ADDITIONAL_INFO_MAP: {
  [key in VisibleLevels]: ProductAdditionalInfo;
} = {
  FREE: {
    level: 0,
    displayName: 'Free',
    description: 'Create designs for free.',
    featureTitle: 'Features:',
    features: [
      '2 CAD designs',
      'Export to PNG/PDF',
      'Shape Libraries (Basic)',
      'Available for Android, and Microsoft Windows',
    ],
    popular: false,
  },
  BASIC: {
    level: 1,
    displayName: 'Draw Basic',
    description:
      'Powerful and precise CAD drawing solution for individual users.',
    featureTitle: 'Everything in Free plan, plus:',
    features: [
      'Unlimited Projects & Drawings',
      'Cloud Backup',
      'Shape Libraries (Basic)',
      'Export to PNG/PDF',
      'Shareable Link',
      'AR Room Scanner (only for iOS Pro devices)',
      'Community & Help Center Access',
      'Build on iOS, Android, Windows & Mac',
    ],
    popular: false,
  },
  PROFESSIONAL: {
    level: 10,
    displayName: 'Draw Pro',
    description:
      'Optimized for Pros: Advanced features, robust exports, and basic integrations.',
    featureTitle: 'Everything in Basic plan, plus:',
    features: [
      'Advanced Cloud Features',
      'Create Layers',
      'Create Custom Shapes',
      'Location Based Photos & Mark-up',
      'Import and Mark-up PDFs',
      'Export to PNG/PDF/DXF/DWG',
      'Basic Software Integrations',
    ],
    popular: false,
  },
  TAKEOFF_BASIC: {
    level: 20,
    subTitle: 'Draw and Calculate',
    displayName: 'Takeoff',
    description:
      'Efficient, accurate takeoffs enhanced by premium integrations and support',
    featureTitle: 'Everything in Pro plan, plus: ',
    features: [
      'Unlimited Material Takeoffs',
      'Custom Forms',
      'Customizable Product Library',
      'Premium Software Integrations',
      'Dedicated Support',
    ],
    popular: false,
  },
  PREMIUM_PLUS: {
    level: 30,
    subTitle: 'Sealing More Deals',
    displayName: 'Estimate',
    featureTitle: 'Everything in the Takeoff plan, plus:',
    description:
      'Instantly create and manage accurate proposals and payments on-site',
    features: [
      'Unlimited Proposals & Estimates',
      'Digital Proposal Acceptance',
      'Customized Proposal Template Builder',
      'Enhanced Sales Presentation Features',
      'Accept On-Site Payments',
      'Proposal Analytics Dashboard',
      'Personalized Support, Training & Onboarding',
    ],
    popular: true,
  },
};

export function filterAndMapVisibleProducts(
  products: Product[] | ProductWithAdditionalInfo[],
  freeVisible: boolean
) {
  const mapped = products
    .filter(product => !HIDDEN_LEVELS.includes(product.level_name))
    .map(product => ({
      ...product,
      ...PRODUCT_LEVEL_ADDITIONAL_INFO_MAP[
        product.level_name as keyof typeof PRODUCT_LEVEL_ADDITIONAL_INFO_MAP
      ],
    }));
  if (!freeVisible) {
    mapped.shift();
  }
  return mapped;
}

export function filterVisibleProducts(products: Product[]) {
  return products.filter(
    product => !HIDDEN_LEVELS.includes(product.level_name)
  );
}
